<script lang="ts" setup>
  import { Motion } from 'motion-v'
  import { computed, onMounted, ref, watch } from 'vue'

  interface Props {
    /**
     * Controls whether the content is expanded or collapsed
     */
    isOpen: boolean

    /**
     * Duration of the animation in seconds
     * @default 0.3
     */
    duration?: number

    /**
     * Whether to animate on initial mount
     * @default false
     */
    animateOnMount?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    duration: 0.3,
    animateOnMount: false
  })

  // Ref to track content height
  const contentRef = ref<HTMLElement | null>(null)
  const contentHeight = ref(0)
  const isInitialMount = ref(true)
  const isVisible = ref(props.isOpen)
  const isSettled = ref(false)

  // Update content height whenever content changes
  const updateHeight = (): void => {
    if (contentRef.value) {
      contentHeight.value = contentRef.value.scrollHeight
    }
  }

  // Animation states
  const animationState = computed(() => {
    // If we're in initial mount and shouldn't animate, skip animation
    if (isInitialMount.value && !props.animateOnMount) {
      return {
        height: props.isOpen ? 'auto' : '0px',
        opacity: props.isOpen ? 1 : 0,
        overflow: 'hidden'
      }
    }

    return {
      height: props.isOpen ? 'auto' : '0px',
      opacity: props.isOpen ? 1 : 0,
      overflow: props.isOpen && isSettled.value ? 'unset' : 'hidden'
    }
  })

  // Animation transition
  const transitionConfig = computed(() => ({
    type: 'spring',
    stiffness: 300,
    damping: 30,
    duration: props.duration,
    onComplete: () => {
      // After animation completes, update visibility for accessibility
      isVisible.value = props.isOpen
      isSettled.value = props.isOpen
    }
  }))

  // Watch for changes to isOpen
  watch(
    () => props.isOpen,
    (newValue) => {
      isSettled.value = false
      if (newValue) {
        // Make element visible before expanding
        isVisible.value = true
        // Update height measurement
        updateHeight()
      }

      // No longer initial mount after first change
      isInitialMount.value = false
    }
  )

  // Track content changes to update height
  const resizeObserver = ref<ResizeObserver | null>(null)

  onMounted(() => {
    // Set up resize observer to track content height changes
    if (contentRef.value) {
      updateHeight()

      resizeObserver.value = new ResizeObserver(() => {
        if (props.isOpen) {
          updateHeight()
        }
      })

      resizeObserver.value.observe(contentRef.value)

      // After initial render, set initial mount to false
      setTimeout(() => {
        isInitialMount.value = false
        isSettled.value = props.isOpen
      }, 0)
    }
  })

  // Clean up the observer when component is destroyed
  onBeforeUnmount(() => {
    if (resizeObserver.value) {
      resizeObserver.value.disconnect()
    }
  })
</script>

<template>
  <Motion :animate="animationState" :transition="transitionConfig">
    <div ref="contentRef" :aria-hidden="!isOpen" :class="{ hidden: !isVisible && !isOpen }">
      <slot />
    </div>
  </Motion>
</template>
